import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '@workos-inc/authkit-react';

import Confetti from '../../components/Confetti';
import Icon from '../../components/Icon';
import { TOGGLE_BAND } from '../../components/viewToggleMetrics';
import { ApiError } from '../../shared/api';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import BackdropHeader from './BackdropHeader';
import BackdropPage from './BackdropPage';
import { Emoji } from './accountParts';
import { CARD_INK, QUESTION_BG } from './backdrop';
import type { OnboardingAnswers, OnboardingPatch } from './accountApi';
import { useMe } from './meContext';
import { PRIVACY_URL, TERMS_URL } from './legalLinks';
import {
  COPY,
  DESCRIPTORS,
  MAX_PICKS,
  ORIGINS,
  OTHER_MAX,
  REASONS,
  SOMETHING_ELSE,
  TERMS_VERSION,
  type Option,
} from './onboardingCopy';
import { CARD_WIDTH, DESK_SCALE, PHONE_SCALE, type OnboardingScale } from './onboardingMetrics';
import { rememberOrigin } from './originStore';
import { ACCOUNT_PATHS, ACCOUNT_ROOT } from './paths';
import { HOME, returnToFrom } from './returnTo';

/**
 * The sign-up questions (CONTRACT §5), shown once after WorkOS sends a new
 * account back, and again from the Account page to edit them (`?edit=1`).
 *
 * Five steps since Sept 19 — welcome, who you are, where you're based,
 * what you want, and you're all set — on the same photograph the account
 * page uses, after Sam's four mobile mockups and the desktop one. The SMS step
 * is gone; see `onboardingCopy.ts` for what was parked rather than deleted.
 *
 * Answers live in this component's state and on the server, nowhere else: not
 * in storage, the URL, telemetry or analytics (D10). The one exception is the
 * where-you're-based answer, which the server will not take yet and which is
 * held in memory for the tab's lifetime so the account page can show it — see `originStore.ts`.
 */

type Step = 'terms' | 'descriptors' | 'origin' | 'reasons' | 'done';

const FIRST_TIME: Step[] = ['terms', 'descriptors', 'origin', 'reasons', 'done'];
/** Editing never reaches "You're all set!": that moment belongs to signing up. */
const EDITING: Step[] = ['descriptors', 'origin', 'reasons'];

/** Where a returning, unfinished account picks up. */
function resumeAt(answers: OnboardingAnswers, heldOrigin: string | null): Step {
  if (!answers.termsAcceptedAt) return 'terms';
  if (answers.descriptors.length === 0) return 'descriptors';
  if (!(answers.origin ?? heldOrigin) && !answers.originSkipped) return 'origin';
  return 'reasons';
}

const MUTED = 'rgba(54,47,42,0.68)';

/** Step 5's four seconds, and the two fades at its ends. */
const DONE_IN_MS = 300;
const DONE_HOLD_MS = 4000;
const DONE_OUT_MS = 700;

/* ── Parts ──────────────────────────────────────────────────────────────── */

/**
 * The cards, centred in the room above where a phone's view toggle sits. The
 * band is kept clear even though this page does not draw the toggle — it is
 * where Sam's mockups put the card, and the question should not move between
 * signing up and coming back to edit an answer.
 */
const Middle = styled('main')({
  position: 'relative',
  minHeight: `calc(100dvh - ${TOGGLE_BAND})`,
  display: 'flex',
  flexDirection: 'column',
  // `safe` so a card taller than the screen is scrolled to rather than clipped
  // at the top, which plain centring in a scroller does.
  justifyContent: 'safe center',
  alignItems: 'center',
  gap: 18,
  // Clear of the header, which is drawn over the page rather than above it.
  padding: '62px 0 56px',
  boxSizing: 'border-box',
});

const Card = styled('form')({
  position: 'relative',
  boxSizing: 'border-box',
  width: CARD_WIDTH,
  background: QUESTION_BG,
  color: CARD_INK,
});

const Dots = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  gap: 6,
  marginBottom: 14,
  '& span': { width: 6, height: 6, borderRadius: 3, background: 'rgba(54,47,42,0.2)' },
  '& span[data-on="true"]': { background: CARD_INK, width: 18 },
});

const Title = styled('h1')({
  // Wider than the card's padding: the mockups let the question run closer to
  // the edge than the option rows do, which is what keeps it to two lines.
  margin: '0 -6px',
  fontFamily: FONTS.serif,
  fontWeight: 700,
  letterSpacing: '-0.005em',
  textAlign: 'center',
  textWrap: 'balance',
  '&:focus': { outline: 'none' },
});

const Helper = styled('p')({
  margin: '6px 0 0',
  fontFamily: FONTS.body,
  lineHeight: 1.4,
  textAlign: 'center',
  color: MUTED,
});

const Lead = styled('p')({
  margin: '18px 0 12px',
  fontFamily: FONTS.display,
  fontWeight: 700,
});

const Rows = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

/** One option: the emoji at the left, then the words. */
const Row = styled('label')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  padding: '0 16px 0 10px',
  background: '#f7f4ee',
  border: '1.5px solid transparent',
  cursor: 'pointer',
  transition: 'background 120ms ease, border-color 120ms ease, opacity 120ms ease',
  '&:hover': { background: '#fff' },
  '&[data-checked="true"]': { borderColor: CARD_INK, background: '#fff' },
  '&[data-disabled="true"]': { opacity: 0.45, cursor: 'default' },
  '&[data-disabled="true"]:hover': { background: '#f7f4ee' },
  '&:has(input:focus-visible)': { outline: `2px solid ${BRAND.dustyOlive}`, outlineOffset: 2 },
  '&[data-focus="true"]': { outline: `2px solid ${BRAND.dustyOlive}`, outlineOffset: 2 },
});

/** The real control: invisible, but it is what is focused, read and ticked. */
const HiddenInput = styled('input')({
  position: 'absolute',
  opacity: 0,
  width: 1,
  height: 1,
  margin: 0,
  pointerEvents: 'none',
});

const RowWords = styled('span')({
  display: 'block',
  minWidth: 0,
  marginLeft: 11,
});

const RowLabel = styled('span')({
  display: 'block',
  fontFamily: FONTS.display,
  fontWeight: 700,
  lineHeight: 1.2,
});

const RowDescription = styled('span')({
  display: 'block',
  marginTop: 1,
  fontFamily: FONTS.body,
  lineHeight: 1.3,
  color: MUTED,
});

const TextInput = styled('input')({
  width: '100%',
  boxSizing: 'border-box',
  minHeight: 44,
  padding: '10px 14px',
  borderRadius: 12,
  border: '1.5px solid rgba(54,47,42,0.28)',
  background: '#fff',
  color: CARD_INK,
  // 16px or iOS zooms the page on focus.
  fontFamily: FONTS.body,
  fontSize: 16,
  '&::placeholder': { color: 'rgba(54,47,42,0.45)' },
  '&:focus': { outline: 'none', borderColor: CARD_INK },
});

const Count = styled('p')({
  margin: '4px 4px 0',
  fontFamily: FONTS.body,
  fontSize: 12.5,
  textAlign: 'right',
  color: MUTED,
});

/**
 * The footer, on the grass under the card: Back, Skip, and the primary pill.
 * Parchment, not the card's off-black — the mockups put it on grass dark
 * enough that off-black disappears into it.
 */
const Foot = styled('div')({
  display: 'flex',
  alignItems: 'center',
  width: CARD_WIDTH,
  fontFamily: FONTS.display,
  fontWeight: 700,
  color: BRAND.parchment,
});

const FootLink = styled('button')({
  appearance: 'none',
  border: 'none',
  background: 'none',
  padding: '8px 6px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontWeight: 700,
  color: 'inherit',
  '&:hover:not(:disabled)': { opacity: 0.7 },
  '&:disabled': { opacity: 0.4, cursor: 'default' },
});

const Pill = styled('button')({
  appearance: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 30px',
  borderRadius: 999,
  border: 'none',
  cursor: 'pointer',
  fontFamily: FONTS.display,
  fontWeight: 700,
  background: BRAND.parchment,
  color: CARD_INK,
  transition: 'background 120ms ease, opacity 120ms ease',
  '&:hover:not(:disabled)': { background: '#fff' },
  '&:disabled': { opacity: 0.45, cursor: 'default' },
});

const ErrorLine = styled('p')({
  margin: '16px 0 0',
  padding: '10px 12px',
  borderRadius: 10,
  fontFamily: FONTS.body,
  fontSize: 14,
  lineHeight: 1.45,
  background: 'rgba(239,61,35,0.12)',
  border: '1px solid rgba(239,61,35,0.55)',
});

/** "Cancel" / "Log out", under everything else. */
const Below = styled('div')({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 10,
  display: 'flex',
  justifyContent: 'center',
  '& button': {
    appearance: 'none',
    border: 'none',
    background: 'none',
    padding: '6px 10px',
    cursor: 'pointer',
    fontFamily: FONTS.display,
    fontWeight: 700,
    fontSize: 14,
    color: 'rgba(243,238,231,0.85)',
  },
  '& button:hover': { color: BRAND.parchment },
});

const CheckBox = styled('span')({
  flexShrink: 0,
  borderRadius: 6,
  border: '1.5px solid rgba(54,47,42,0.5)',
  background: '#fff',
  display: 'grid',
  placeItems: 'center',
  color: BRAND.parchment,
  '[data-checked="true"] &': { background: CARD_INK, borderColor: CARD_INK },
});

const Check = () => (
  <svg width={13} height={13} viewBox="0 0 14 14" aria-hidden="true" focusable="false">
    <path
      d="M2.5 7.4 5.6 10.4 11.6 3.6"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── The option list ────────────────────────────────────────────────────── */

interface RowsProps {
  options: Option[];
  scale: OnboardingScale;
  picked: string[];
  onChange: (next: string[]) => void;
  labelledBy: string;
  describedBy?: string;
}

function CheckRows({
  options,
  scale,
  picked,
  onChange,
  other,
  onOther,
  otherPlaceholder,
  labelledBy,
  describedBy,
}: RowsProps & { other: string; onOther: (v: string) => void; otherPlaceholder: string }) {
  const full = picked.length >= MAX_PICKS;
  const otherRef = useRef<HTMLInputElement | null>(null);
  const [focusOther, setFocusOther] = useState(false);

  useEffect(() => {
    if (focusOther) {
      otherRef.current?.focus();
      setFocusOther(false);
    }
  }, [focusOther]);

  return (
    <Rows role="group" aria-labelledby={labelledBy} aria-describedby={describedBy} style={{ gap: scale.rowGap }}>
      {options.map((option) => {
        const checked = picked.includes(option.key);
        const disabled = !checked && full;
        return (
          <div key={option.key} style={{ display: 'contents' }}>
            <Row
              data-checked={checked}
              data-disabled={disabled}
              style={{ minHeight: scale.rowHeight, borderRadius: scale.rowRadius }}
            >
              <HiddenInput
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => {
                  if (checked) onChange(picked.filter((k) => k !== option.key));
                  else {
                    onChange([...picked, option.key]);
                    if (option.key === SOMETHING_ELSE) setFocusOther(true);
                  }
                }}
              />
              <Emoji style={{ fontSize: scale.emoji }}>{option.emoji}</Emoji>
              <RowWords>
                <RowLabel style={{ fontSize: scale.label }}>{option.label}</RowLabel>
                {option.description && (
                  <RowDescription style={{ fontSize: scale.description }}>{option.description}</RowDescription>
                )}
              </RowWords>
            </Row>
            {option.key === SOMETHING_ELSE && checked && (
              <div>
                <TextInput
                  ref={otherRef}
                  value={other}
                  maxLength={OTHER_MAX}
                  placeholder={otherPlaceholder}
                  aria-label={`${option.label.replace(/…$/, '')}: in your own words`}
                  // Browsers remember what is typed into named fields; this stays with us.
                  autoComplete="off"
                  onChange={(e) => onOther(e.target.value)}
                />
                {other.length > OTHER_MAX - 40 && (
                  <Count>
                    {other.length} / {OTHER_MAX}
                  </Count>
                )}
              </div>
            )}
          </div>
        );
      })}
    </Rows>
  );
}

/**
 * The travel question. Radio semantics rather than checkboxes, so that a screen
 * reader is told in as many words that only one answer is taken: one stop on
 * the tab ring, arrow keys moving the choice (Sam, Sept 19).
 */
function RadioRows({ options, scale, picked, onChange, labelledBy }: RowsProps) {
  const chosen = picked[0] ?? null;
  const index = Math.max(0, options.findIndex((o) => o.key === chosen));
  const refs = useRef<(HTMLElement | null)[]>([]);

  const move = (to: number) => {
    const next = (to + options.length) % options.length;
    onChange([options[next].key]);
    refs.current[next]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      move(index + 1);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      move(index - 1);
    } else if (e.key === ' ') {
      e.preventDefault();
      onChange([options[index].key]);
    }
  };

  return (
    <Rows role="radiogroup" aria-labelledby={labelledBy} style={{ gap: scale.rowGap }}>
      {options.map((option, i) => {
        const checked = option.key === chosen;
        return (
          <Row
            key={option.key}
            as="div"
            role="radio"
            aria-checked={checked}
            // One stop on the tab ring for the whole group, as a radio group has.
            tabIndex={checked || (!chosen && i === 0) ? 0 : -1}
            ref={(node: HTMLElement | null) => {
              refs.current[i] = node;
            }}
            data-checked={checked}
            onClick={() => onChange([option.key])}
            onKeyDown={onKeyDown}
            style={{ minHeight: scale.rowHeight, borderRadius: scale.rowRadius }}
          >
            <Emoji style={{ fontSize: scale.emoji }}>{option.emoji}</Emoji>
            <RowWords>
              <RowLabel style={{ fontSize: scale.label }}>{option.label}</RowLabel>
            </RowWords>
          </Row>
        );
      })}
    </Rows>
  );
}

function Progress({ steps, at }: { steps: Step[]; at: Step }) {
  const index = steps.indexOf(at);
  return (
    <Dots aria-hidden="true">
      {steps.map((s, i) => (
        <span key={s} data-on={i <= index} />
      ))}
    </Dots>
  );
}

const readyToSend = (picked: string[], other: string) =>
  picked.length > 0 && (!picked.includes(SOMETHING_ELSE) || other.trim().length > 0);

/* ── "You're all set!" ──────────────────────────────────────────────────── */

/**
 * Fades in quickly on an accelerating ease, bursts, holds, then fades out more
 * slowly and takes the user on. The timers drive the navigation rather than the
 * transitions, so `prefers-reduced-motion` — which cuts every transition in the
 * app to 0.01ms (`fonts.css`) — shortens the animation without stranding
 * anyone on a card that never leaves.
 */
function AllSet({ scale, onDone }: { scale: OnboardingScale; onDone: () => void }) {
  const [shown, setShown] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [burst, setBurst] = useState(0);

  useEffect(() => {
    const timers = [
      window.setTimeout(() => setShown(true), 30),
      window.setTimeout(() => setBurst(1), DONE_IN_MS + 40),
      window.setTimeout(() => setLeaving(true), DONE_HOLD_MS),
      window.setTimeout(onDone, DONE_HOLD_MS + DONE_OUT_MS),
    ];
    return () => timers.forEach(window.clearTimeout);
  }, [onDone]);

  return (
    <Card
      as="div"
      role="status"
      style={{
        borderRadius: scale.radius,
        padding: `${scale.pad + 8}px ${scale.pad}px ${scale.pad + 10}px`,
        textAlign: 'center',
        opacity: leaving ? 0 : shown ? 1 : 0,
        transition: leaving
          ? `opacity ${DONE_OUT_MS}ms ease-out`
          : `opacity ${DONE_IN_MS}ms ease-in`,
      }}
    >
      {/* Sized to the card's perimeter rather than a button's. */}
      <Confetti trigger={burst} pieces={30} reach={Math.round(scale.title * 5)} size={6} durationMs={1100} />
      <Title style={{ fontSize: scale.title, lineHeight: scale.titleLine }}>{COPY.done.title}</Title>
      <p
        style={{
          margin: '16px 0 0',
          fontFamily: FONTS.display,
          fontWeight: 700,
          fontSize: scale.body,
          lineHeight: 1.3,
        }}
      >
        {COPY.done.lede}
      </p>
    </Card>
  );
}

/* ── The page ───────────────────────────────────────────────────────────── */

export default function OnboardingPage() {
  const { status, api, refresh, markOnboardingComplete } = useMe();
  const { signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editing = params.get('edit') === '1';
  const wantedStep = params.get('step');
  const returnTo = useMemo(() => returnToFrom(location.state) ?? HOME, [location.state]);
  const scale = window.innerWidth < 900 ? PHONE_SCALE : DESK_SCALE;

  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [step, setStep] = useState<Step | null>(null);

  const [agreed, setAgreed] = useState(false);
  const [descriptors, setDescriptors] = useState<string[]>([]);
  const [descriptorOther, setDescriptorOther] = useState('');
  const [origin, setOrigin] = useState<string[]>([]);
  const [reasons, setReasons] = useState<string[]>([]);
  const [reasonOther, setReasonOther] = useState('');

  const [saving, setSaving] = useState<'submit' | 'skip' | null>(null);
  const [saveError, setSaveError] = useState('');

  /**
   * Finished before this visit, rather than finished during it. The reasons
   * step saves `complete: true` and then shows "You're all set!"; without this
   * the page would see `completedAt` appear and navigate away mid-confetti.
   */
  const arrivedComplete = useRef(false);

  const titleId = useId();
  const helperId = useId();
  const title = useRef<HTMLHeadingElement | null>(null);

  const ready = status.kind === 'ready';
  const userId = ready ? status.me.id : '';

  // The user's own answers, to resume or to edit.
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    api
      .onboarding()
      .then((found) => {
        if (cancelled) return;
        arrivedComplete.current = Boolean(found.completedAt);
        setLoadFailed(false);
        setAgreed(Boolean(found.termsAcceptedAt));
        setDescriptors(found.descriptors);
        setDescriptorOther(found.descriptorOther ?? '');
        setOrigin(found.origin ? [found.origin] : []);
        setReasons(found.reasons);
        setReasonOther(found.reasonOther ?? '');
        const asked = EDITING.find((s) => s === wantedStep);
        setStep(editing ? (asked ?? 'descriptors') : resumeAt(found, null));
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [ready, api, editing, wantedStep, attempt]);

  // A new screen is a new page as far as a screen reader is concerned.
  useEffect(() => {
    if (step) title.current?.focus();
  }, [step]);

  const steps = editing ? EDITING : FIRST_TIME;

  if (status.kind === 'logged-out') {
    return (
      <Navigate
        to={ACCOUNT_PATHS.logIn}
        replace
        state={{ returnTo: { path: `${location.pathname}${location.search}` } }}
      />
    );
  }

  // Finished already, and not here to edit: nothing to ask.
  if (!editing && arrivedComplete.current) {
    return <Navigate to={returnTo.path} replace state={returnTo.arrival ?? null} />;
  }

  const leave = () => {
    if (editing) navigate(ACCOUNT_ROOT);
    else {
      try {
        signOut({ returnTo: 'https://myclimateweek.com/' });
      } catch {
        window.location.assign(import.meta.env.BASE_URL);
      }
    }
  };

  /**
   * Saves one step, and quietly works around the two things today's API does
   * not accept yet.
   *
   * `PATCH /me/onboarding` validates with a strict zod schema, so an unknown
   * field or an unknown enum value fails the whole step rather than being
   * ignored. Both gaps are the server being older than the question, which is
   * not the user's problem: on a 400 the call is retried once without the new
   * field, or with the new descriptor key mapped down to the one it was split
   * out of. The day the backend lands them, the first attempt succeeds and
   * nothing here changes.
   */
  const save = async (patch: OnboardingPatch, which: 'submit' | 'skip'): Promise<boolean> => {
    setSaving(which);
    setSaveError('');

    const withoutOrigin = (): OnboardingPatch | null => {
      if (patch.origin === undefined && patch.skipOrigin === undefined) return null;
      const rest: OnboardingPatch = { ...patch };
      delete rest.origin;
      delete rest.skipOrigin;
      return rest;
    };
    const withoutNewDescriptor = (): OnboardingPatch | null => {
      if (!patch.descriptors?.includes('government-public-sector')) return null;
      return {
        ...patch,
        // The nearest existing key, and the one it was split out of, so the
        // answer persists and `complete: true` never strands on an empty array.
        descriptors: [
          ...new Set(
            patch.descriptors.map((k) =>
              k === 'government-public-sector' ? 'policy-government-advocacy' : k,
            ),
          ),
        ],
      };
    };

    try {
      try {
        await api.saveOnboarding(patch);
      } catch (err) {
        const status400 = err instanceof ApiError && err.status === 400;
        const fallback = status400 ? (withoutOrigin() ?? withoutNewDescriptor()) : null;
        if (!fallback) throw err;
        if (import.meta.env.DEV) {
          console.warn('[onboarding] the API refused a Sept 19 field; retrying without it', err);
        }
        await api.saveOnboarding(fallback);
      }
      return true;
    } catch (err) {
      const code = (err as { code?: string | null }).code;
      if (code === 'TERMS_REQUIRED') {
        setAgreed(false);
        setStep('terms');
        setSaveError('Please agree to the terms first.');
      } else {
        setSaveError('We couldn’t save that. Check your connection and try again.');
      }
      return false;
    } finally {
      setSaving(null);
    }
  };

  const finish = () => {
    if (editing) {
      navigate(ACCOUNT_ROOT);
      return;
    }
    markOnboardingComplete();
    // The account's data layer starts moving this device's plans in (§6).
    window.dispatchEvent(new CustomEvent('mcw:onboarding-complete'));
    // Somewhere specific if the user was on their way there; otherwise the
    // account page, which is where "You're all set!" hands them off (Sept 19).
    const to = returnTo.path === HOME.path && !returnTo.arrival ? ACCOUNT_ROOT : returnTo.path;
    navigate(to, { replace: true, state: returnTo.arrival ?? null });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving || !step) return;

    if (step === 'terms') {
      if (!agreed) return;
      if (await save({ acceptTerms: { version: TERMS_VERSION } }, 'submit')) setStep('descriptors');
      return;
    }

    if (step === 'descriptors') {
      if (!readyToSend(descriptors, descriptorOther)) return;
      const other = descriptors.includes(SOMETHING_ELSE) ? descriptorOther.trim() : null;
      if (await save({ descriptors, descriptorOther: other }, 'submit')) {
        setStep(editing && wantedStep === 'descriptors' ? 'origin' : 'origin');
      }
      return;
    }

    if (step === 'origin') {
      if (origin.length === 0) return;
      rememberOrigin(userId, origin[0]);
      if (await save({ origin: origin[0] }, 'submit')) setStep('reasons');
      return;
    }

    // Reasons: the last question, and where signing up completes.
    if (!readyToSend(reasons, reasonOther)) return;
    const other = reasons.includes(SOMETHING_ELSE) ? reasonOther.trim() : null;
    const patch: OnboardingPatch = editing
      ? { reasons, reasonOther: other }
      : { reasons, reasonOther: other, complete: true };
    if (!(await save(patch, 'submit'))) return;
    if (editing) finish();
    else setStep('done');
  };

  const skip = async () => {
    if (saving || !step) return;
    if (step === 'descriptors') {
      setStep('origin');
      return;
    }
    if (step === 'origin') {
      rememberOrigin(userId, null, true);
      if (await save({ skipOrigin: true }, 'skip')) setStep('reasons');
      return;
    }
    // Reasons: skipping here is what finishes signing up.
    if (editing) {
      finish();
      return;
    }
    if (await save({ complete: true }, 'skip')) setStep('done');
  };

  const goBack = () => {
    setSaveError('');
    if (step === 'reasons') setStep('origin');
    else if (step === 'origin') setStep('descriptors');
    else if (step === 'descriptors' && !editing) setStep('terms');
  };

  /* ── What the card holds ─────────────────────────────────────────────── */

  const cardStyle = { borderRadius: scale.radius, padding: `${scale.pad}px ${scale.pad}px ${scale.pad + 3}px` };
  const titleStyle = { fontSize: scale.title, lineHeight: scale.titleLine };

  let card: ReactNode;
  /** Step 1's second card, which the mockup draws below the first. */
  let second: ReactNode = null;
  let foot: ReactNode = null;

  if (status.kind === 'error' || loadFailed) {
    card = (
      <Card as="div" style={cardStyle}>
        <Title ref={title} tabIndex={-1} style={titleStyle}>
          We couldn&rsquo;t reach your account
        </Title>
        <Helper style={{ fontSize: scale.helper }}>Check your connection and try again.</Helper>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 22 }}>
          <Pill
            type="button"
            style={{ minHeight: scale.pill, fontSize: scale.pillText }}
            onClick={() => {
              refresh();
              setAttempt((n) => n + 1);
            }}
          >
            Try again
          </Pill>
        </div>
      </Card>
    );
  } else if (!step) {
    card = (
      <Card as="div" style={{ ...cardStyle, display: 'grid', placeItems: 'center', minHeight: 220 }} role="status">
        <CircularProgress sx={{ color: CARD_INK }} aria-label="Loading" />
      </Card>
    );
  } else if (step === 'done') {
    card = <AllSet scale={scale} onDone={finish} />;
  } else {
    let body: ReactNode;
    let primary = 'Next';
    let submitReady: boolean;

    if (step === 'terms') {
      primary = COPY.terms.go;
      submitReady = agreed;
      body = (
        <>
          <Title ref={title} tabIndex={-1} id={titleId} style={titleStyle}>
            {COPY.terms.title}
          </Title>
          <div
            style={{
              width: scale.lockup,
              margin: '10px auto 0',
              color: CARD_INK,
            }}
          >
            <Icon name="logo:mcw" size="100%" title="My Climate Week" />
          </div>
          <p
            style={{
              margin: `${scale.pad}px 0 0`,
              fontFamily: FONTS.display,
              fontWeight: 700,
              fontSize: scale.body * 0.8,
              lineHeight: 1.35,
            }}
          >
            <strong>My Climate Week</strong> {COPY.terms.project}{' '}
            <a
              href="https://www.commonground.boston/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', fontWeight: 700 }}
            >
              CommonGround.
            </a>
          </p>

          <Row
            data-checked={agreed}
            style={{
              marginTop: scale.pad,
              padding: '10px 12px',
              borderRadius: 14,
              background: 'transparent',
              alignItems: 'flex-start',
              gap: 12,
            }}
          >
            <HiddenInput type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            <span
              style={{
                flex: 1,
                fontFamily: FONTS.body,
                fontSize: scale.fine,
                lineHeight: 1.45,
              }}
            >
              {COPY.terms.agree}{' '}
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                Terms of Use
              </a>{' '}
              and{' '}
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                Privacy Policy
              </a>
              .
            </span>
            <CheckBox style={{ width: scale.checkbox, height: scale.checkbox }} aria-hidden="true">
              {agreed && <Check />}
            </CheckBox>
          </Row>
        </>
      );
    } else if (step === 'descriptors') {
      submitReady = readyToSend(descriptors, descriptorOther);
      body = (
        <>
          <Progress steps={steps} at={step} />
          <Title ref={title} tabIndex={-1} id={titleId} style={titleStyle}>
            {COPY.descriptors.title}
          </Title>
          <Helper id={helperId} style={{ fontSize: scale.helper }}>
            {COPY.descriptors.helper}
          </Helper>
          <Lead style={{ fontSize: scale.lead }}>{COPY.descriptors.lead}</Lead>
          <CheckRows
            options={DESCRIPTORS}
            scale={scale}
            picked={descriptors}
            onChange={setDescriptors}
            other={descriptorOther}
            onOther={setDescriptorOther}
            otherPlaceholder={COPY.descriptors.otherPlaceholder}
            labelledBy={titleId}
            describedBy={helperId}
          />
        </>
      );
    } else if (step === 'origin') {
      submitReady = origin.length > 0;
      body = (
        <>
          <Progress steps={steps} at={step} />
          <Title ref={title} tabIndex={-1} id={titleId} style={titleStyle}>
            {COPY.origin.title}
          </Title>
          {/* No "Select up to 3": this question takes one answer. */}
          <Lead style={{ fontSize: scale.lead, marginTop: 22 }}>{COPY.origin.lead}</Lead>
          <RadioRows
            options={ORIGINS}
            scale={scale}
            picked={origin}
            onChange={setOrigin}
            labelledBy={titleId}
          />
        </>
      );
    } else {
      primary = editing ? 'Save' : 'Submit';
      submitReady = readyToSend(reasons, reasonOther);
      body = (
        <>
          <Progress steps={steps} at={step} />
          <Title ref={title} tabIndex={-1} id={titleId} style={titleStyle}>
            {COPY.reasons.title}
          </Title>
          <Helper id={helperId} style={{ fontSize: scale.helper }}>
            {COPY.reasons.helper}
          </Helper>
          <Lead style={{ fontSize: scale.lead }}>{COPY.reasons.lead}</Lead>
          <CheckRows
            options={REASONS}
            scale={scale}
            picked={reasons}
            onChange={setReasons}
            other={reasonOther}
            onOther={setReasonOther}
            otherPlaceholder={COPY.reasons.otherPlaceholder}
            labelledBy={titleId}
            describedBy={helperId}
          />
        </>
      );
    }

    card = (
      <Card id="onboarding-card" onSubmit={submit} noValidate aria-labelledby={titleId} style={cardStyle}>
        {body}
        {saveError && <ErrorLine role="alert">{saveError}</ErrorLine>}
      </Card>
    );

    if (step === 'terms') {
      second = (
        <Card
          as="div"
          style={{ ...cardStyle, padding: `${scale.pad}px ${scale.pad}px ${scale.pad + 2}px` }}
        >
          <p
            style={{
              margin: 0,
              fontFamily: FONTS.display,
              fontWeight: 700,
              fontSize: scale.body,
              lineHeight: 1.35,
            }}
          >
            {COPY.terms.ask}
          </p>
        </Card>
      );
    }

    foot =
      step === 'terms' ? (
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Pill
            type="submit"
            form="onboarding-card"
            disabled={!submitReady || saving !== null}
            style={{ minHeight: scale.pill, fontSize: scale.pillText }}
          >
            {saving === 'submit' ? 'Saving…' : primary}
          </Pill>
        </div>
      ) : (
        <Foot style={{ fontSize: scale.foot }}>
          <FootLink type="button" onClick={goBack} disabled={saving !== null || (editing && step === 'descriptors')}>
            Back
          </FootLink>
          <FootLink
            type="button"
            onClick={skip}
            disabled={saving !== null}
            aria-busy={saving === 'skip'}
            style={{ marginLeft: 26 }}
          >
            {saving === 'skip' ? 'Saving…' : 'Skip'}
          </FootLink>
          <Pill
            type="submit"
            form="onboarding-card"
            disabled={!submitReady || saving !== null}
            aria-busy={saving === 'submit'}
            style={{ marginLeft: 'auto', minHeight: scale.pill, fontSize: scale.pillText, minWidth: '43%' }}
          >
            {saving === 'submit' ? 'Saving…' : primary}
          </Pill>
        </Foot>
      );
  }

  return (
    <BackdropPage header={<BackdropHeader />}>
      <Middle>
        {card}
        {second}
        {foot}
        {step !== 'done' && (
          <Below>
            <button type="button" onClick={leave}>
              {editing ? 'Cancel' : 'Log out'}
            </button>
          </Below>
        )}
      </Middle>
    </BackdropPage>
  );
}
